import { useApolloClient, useMutation } from '@apollo/client';
import React, { useContext } from 'react';
import { groupBy, sortBy } from 'lodash';
import { MODALS } from '../constants';
import { routeDisplayName } from '@/utilities/routeNames';
import { PlanningContext } from '../context';
import { REMOVE_ROUTE } from '../graphql/mutations';
import { QUERY_PLANNING_DATA } from '../graphql/queries';
import { buildOptimisticRemoveResponse } from '../graphql/optimisticResponses';
import * as Sentry from '@sentry/react';
import {
    Body2,
    DangerButton,
    GridItemRow,
    ModalActions,
    ModalContent,
    ModalTitle,
    PrimaryButton,
    SecondaryButton,
} from '../blocks';
import NavResponsiveModal from '@/components/Navigation/NavResponsiveModal';
import routeStatusOptions from '@/constants/routeStatusOptions';
import { useClientUser } from '@/hooks';
import { buildDeliveryUnassignSet, collectMappingIdsFromRoute } from './removeHelpers';
import { DISPATCH_PLAN_SERIALIZATION_KEY } from '@/graphql/serializingLink';
import { removeRefs } from '@/graphql/util';
import { ORDER_ROUTE_MAPPINGS } from '@/graphql/queries/order_route_mappings';
import {
    ELIGIBILITY,
    appendBucketRows,
    bucketCandidateIds,
    bucketEligibility,
    readBucketOrder,
} from '../graphql/planningBuckets';
import { routeNumberOrder } from '../map/assignToRouteHelpers';

// Runs inside REMOVE_ROUTE's update() (after the document write, so
// update_orders_many.returning is already normalized): filters each freed
// order's slim `routes` mapping list, then re-admits eligible orders into
// pickups/dropoffs using the SAME bucket-eligibility module StopRemoveModal's
// unassign flow uses (../graphql/planningBuckets) — including its
// DISCARD-mapping resolution via cache.readFragment, which a plain JS-object
// comparison can't do (the slim mapping list lacks duplicate_override).
// Returns needsRefetch: true when any candidate can't be proven eligible from
// the cache alone.
const patchOrderRoutesAndCollectBucketRows = (cache, { data, variables }) => {
    const deletedMappings = data?.delete_order_route_mappings?.returning || [];

    for (const [order_id, mappings] of Object.entries(groupBy(deletedMappings, 'order_id'))) {
        cache.modify({
            id: cache.identify({ __typename: 'orders', order_id }),
            fields: {
                routes: (existing = [], { toReference }) => removeRefs(existing, mappings, { toReference }),
            },
        });
    }

    const candidates = bucketCandidateIds(deletedMappings);
    const resolveMapping = (mapping) =>
        cache.readFragment({
            id: cache.identify({ __typename: 'order_route_mappings', mapping_id: mapping.mapping_id }),
            fragment: ORDER_ROUTE_MAPPINGS,
        });

    let needsRefetch = false;
    const eligibleRows = { pickups: [], dropoffs: [] };
    for (const bucket of ['pickups', 'dropoffs']) {
        for (const order_id of candidates[bucket]) {
            const row = readBucketOrder(cache, order_id);
            if (!row) {
                needsRefetch = true;
                continue;
            }
            const eligibility = bucketEligibility(row, { bucket, variables, resolveMapping });
            if (eligibility === ELIGIBILITY.UNRESOLVABLE) {
                needsRefetch = true;
                continue;
            }
            if (eligibility === ELIGIBILITY.ELIGIBLE) {
                eligibleRows[bucket].push(row);
            }
        }
    }

    return { eligibleRows, needsRefetch };
};

const RouteRemoveModal = () => {
    const {
        modalOpen,
        setModalOpen,
        actionableRoute,
        selectedRoute,
        setSelectedRoute,
        setError,
        state: { variables },
    } = useContext(PlanningContext);
    const { accountType } = useClientUser();
    const client = useApolloClient();

    const [removeRoute, { loading }] = useMutation(REMOVE_ROUTE);

    const handleModalClose = () => {
        if (selectedRoute === actionableRoute?.route_id) {
            setSelectedRoute(null);
        }
        setModalOpen(null);
    };

    const convertShellRouteIntoNormalRoute = (r) => {
        const { shell, __typename, orders, stopsByRouteId, resource, route_carrier, route_shipper, ...rest } = r;
        return rest;
    };

    // `keepResource: false` (default) is the original behavior — the route is upserted as status
    // 'deleted', freeing the resource. `keepResource: true` empties the route (orders unassigned,
    // stops removed) but leaves it in place with its status unchanged, so the route and its
    // resource survive. Every other part of the payload — mapping deletes, order unassigns, stop
    // deletes, and the cache patch — is identical; only the upserted route status differs.
    const handleRemoveRoute = ({ keepResource = false } = {}) => {
        const deliveries = actionableRoute.orders
            .filter((mapping) => mapping.type !== 'PICKUP')
            .map((mapping) => mapping.order);

        // Route deletion is an explicit `delete_order_route_mappings` over every
        // mapping on the route; no wh_event is emitted. See ./removeHelpers.js.
        const mappingIdsToDelete = collectMappingIdsFromRoute(actionableRoute);

        let routeUpdate = {};
        if (actionableRoute?.shell) {
            routeUpdate = convertShellRouteIntoNormalRoute(actionableRoute);
        }
        routeUpdate = {
            ...routeUpdate,
            route_id: actionableRoute.route_id,
            status: keepResource ? routeStatusOptions.PLANNING.value : 'deleted',
        };

        const orderUpdates = deliveries.map((order) => ({
            where: { order_id: { _eq: order.order_id } },
            _set: buildDeliveryUnassignSet(order),
        }));

        setSelectedRoute(null);

        // False when the cached planning query was unreadable, or a bucket
        // candidate couldn't be proven eligible from the cache — either case
        // falls back to a network-only read.
        let lastPatchApplied = false;

        removeRoute({
            // Fires from the live board and can race assigns/stop edits on the
            // same route — serialize with the board's other stop mutations.
            context: { serializationKey: DISPATCH_PLAN_SERIALIZATION_KEY },
            variables: {
                route_id: actionableRoute?.route_id,
                route: routeUpdate,
                order_updates: orderUpdates,
                events: [],
                mapping_ids_delete: mappingIdsToDelete,
            },
            optimisticResponse: buildOptimisticRemoveResponse({
                actionableRoute,
                routeUpdate,
                deliveries,
                orderUpdates,
            }),
            update: (cache, { data }) => {
                const { eligibleRows, needsRefetch } = patchOrderRoutesAndCollectBucketRows(cache, {
                    data,
                    variables,
                });

                cache.updateQuery({ query: QUERY_PLANNING_DATA, variables }, (prev) => {
                    if (!prev?.routes) {
                        lastPatchApplied = false;
                        return prev;
                    }
                    lastPatchApplied = !needsRefetch;

                    const deletedRoute = data?.insert_routes_one;
                    let nextRoutes = prev.routes;
                    if (deletedRoute) {
                        const emptied = { ...deletedRoute, stopsByRouteId: [], orders: [] };
                        const exists = prev.routes.some((r) => r.route_id === deletedRoute.route_id);
                        nextRoutes = exists
                            ? prev.routes.map((r) => (r.route_id === deletedRoute.route_id ? { ...r, ...emptied } : r))
                            : sortBy([...prev.routes, { teammateByDriverId: null, ...emptied }], routeNumberOrder);
                    }

                    return {
                        ...prev,
                        routes: nextRoutes,
                        pickups: appendBucketRows(prev.pickups, eligibleRows.pickups),
                        dropoffs: appendBucketRows(prev.dropoffs, eligibleRows.dropoffs),
                    };
                });
            },
            onCompleted: (data) => {
                if (!lastPatchApplied) {
                    client
                        .query({
                            query: QUERY_PLANNING_DATA,
                            variables,
                            fetchPolicy: 'network-only',
                            context: { queryDeduplication: false },
                        })
                        .catch(() => {});
                }
                // Real response only: invalidate the deleted stop entities so
                // any other page's cached ref (e.g. the argument-less
                // stopsByRouteId key) goes dangling and self-heals. No gc()
                // here: on the needsRefetch path it could collect the emptied
                // route's order entities before the fallback query lands,
                // blanking open watchers.
                (data?.delete_stops?.returning || []).forEach((stop) =>
                    client.cache.evict({ id: client.cache.identify(stop) })
                );
            },
            onError: (error) => {
                setError(error, 'Error removing route');
            },
        });

        handleModalClose();
    };

    // A shell carries no orders, so "keep the resource" has nothing to remove.
    const canKeepResource = !actionableRoute?.shell;

    return (
        <NavResponsiveModal open={modalOpen === MODALS.ROUTE_REMOVE} onClose={handleModalClose}>
            <ModalTitle>{`Delete ${routeDisplayName(actionableRoute)}`}</ModalTitle>
            <ModalContent>
                <GridItemRow>
                    <Body2>
                        {canKeepResource
                            ? 'How would you like to delete this route? Its orders will be unassigned either way.'
                            : 'Are you sure you want to delete this route?'}
                    </Body2>
                </GridItemRow>
                {canKeepResource && (
                    <>
                        <GridItemRow>
                            <Body2>
                                <strong>Keep the resource</strong> — remove all orders from the route, but keep the route
                                and its resource.
                            </Body2>
                        </GridItemRow>
                        <GridItemRow>
                            <Body2>
                                <strong>Delete route &amp; resource</strong> — remove all orders and delete the route,
                                freeing the resource.
                            </Body2>
                        </GridItemRow>
                    </>
                )}
            </ModalContent>
            <ModalActions>
                <SecondaryButton disabled={loading} onClick={handleModalClose}>
                    Cancel
                </SecondaryButton>
                {canKeepResource && (
                    <PrimaryButton disabled={loading} onClick={() => handleRemoveRoute({ keepResource: true })}>
                        Keep Resource
                    </PrimaryButton>
                )}
                <DangerButton disabled={loading} onClick={() => handleRemoveRoute({ keepResource: false })}>
                    {canKeepResource ? 'Delete Route & Resource' : 'Delete'}
                </DangerButton>
            </ModalActions>
        </NavResponsiveModal>
    );
};

export default RouteRemoveModal;
